// ./src/utils/api/TicketTypeCouponService.ts
//
// Coupon grants on a ticket type — "this ticket comes with 2 drink vouchers".
// Mirrors the eventsmagic-api TicketTypeCoupon SDL. `where.id` accepts one id or
// many (a bulk update may only change status — server-enforced).
//
// The rules only: the server issues the vouchers when an order completes (`CouponCore.applyTicketGrantsForOrder`).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { COUPON_TYPE_FIELDS, CouponType } from "./CouponTypeService";

/** What the grant quantity is counted per (mirrors boundary COUPON_GRANT_PER). */
export const COUPON_GRANT_PER = {
  TICKET: "ticket",
  ORDER: "order",
  GUEST: "guest",
} as const;

export type CouponGrantPer = (typeof COUPON_GRANT_PER)[keyof typeof COUPON_GRANT_PER];

const FIELDS = `
  id
  ticketTypeId
  couponTypeId
  quantity
  grantPer
  status
  rights
  couponType { ${COUPON_TYPE_FIELDS} }
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query TicketTypeCouponList($filter: TicketTypeCouponFilter, $params: PaginationAndSorting) {
    ticketTypeCouponList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation TicketTypeCouponCreate($params: TicketTypeCouponInput) {
    ticketTypeCouponCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation TicketTypeCouponUpdate($where: TicketTypeCouponWhereInput, $params: TicketTypeCouponInput) {
    ticketTypeCouponUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation TicketTypeCouponRemove($where: TicketTypeCouponWhereInput) {
    ticketTypeCouponRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface TicketTypeCoupon {
  id?: string;
  ticketTypeId?: string | null;
  couponTypeId?: string | null;
  quantity?: number | null;
  grantPer?: string | null;
  status?: number | null;
  rights?: string[] | null;
  couponType?: CouponType | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface TicketTypeCouponInput {
  ticketTypeId?: string | null;
  couponTypeId?: string | null;
  quantity?: number | null;
  grantPer?: string | null;
  status?: number | null;
}

export class TicketTypeCouponService {
  static async list(ticketTypeId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { ticketTypeId: [ticketTypeId], status: statuses },
    });
  }

  static async create(params: TicketTypeCouponInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: TicketTypeCouponInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
